/**
 * NBFC Approval Management Component for Platform Admin
 * Approve/Reject schools for NBFC EMI feature
 */
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NBFCApprovalManager({ token }) {
  const [schools, setSchools] = useState([]);
  const [approvals, setApprovals] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load schools
      const schoolsRes = await fetch(`${API_URL}/api/schools`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const schoolsData = await schoolsRes.json();
      if (schoolsData.success) {
        setSchools(schoolsData.schools || []);
      }

      // Load approvals
      const approvalsRes = await fetch(`${API_URL}/api/padhai-emi/admin/approvals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const approvalsData = await approvalsRes.json();
      if (approvalsData.success) {
        const approvalMap = {};
        approvalsData.approvals?.forEach(a => {
          approvalMap[a.school_id] = a;
        });
        setApprovals(approvalMap);
        setStats(approvalsData.summary || { total: 0, approved: 0, pending: 0 });
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  const toggleApproval = async (schoolId, currentStatus) => {
    setUpdating(schoolId);
    try {
      const res = await fetch(`${API_URL}/api/padhai-emi/admin/approve-school`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          school_id: schoolId,
          approved: !currentStatus,
          remarks: !currentStatus ? 'Approved by Platform Admin' : 'Approval revoked'
        })
      });
      
      const data = await res.json();
      if (data.success) {
        // Update local state
        setApprovals(prev => ({
          ...prev,
          [schoolId]: {
            ...prev[schoolId],
            nbfc_approved: data.nbfc_approved,
            approved_at: new Date().toISOString()
          }
        }));
        
        // Update stats
        setStats(prev => ({
          ...prev,
          approved: data.nbfc_approved ? prev.approved + 1 : prev.approved - 1
        }));
      }
    } catch (err) {
      console.error('Error updating approval:', err);
      alert('Error updating approval status');
    }
    setUpdating(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nbfc-approval-manager">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <h2 className="text-xl font-bold mb-2">🏦 NBFC Approval Management</h2>
        <p className="opacity-80">Approve schools for Padhai EMI feature</p>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-white/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{schools.length}</p>
            <p className="text-sm opacity-80">Total Schools</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-300">{stats.approved}</p>
            <p className="text-sm opacity-80">NBFC Approved</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-300">{schools.length - stats.approved}</p>
            <p className="text-sm opacity-80">Pending</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <h4 className="font-semibold text-yellow-800 mb-2">⚠️ NBFC Approval Rules</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>✅ <strong>Approved Schools:</strong> Parents can see and apply for EMI loans</li>
          <li>🔒 <strong>Unapproved Schools:</strong> Only School Admin sees demo, Parents cannot see EMI option</li>
          <li>📋 <strong>Prerequisite:</strong> School must have Fee Structure configured before approval</li>
        </ul>
      </div>

      {/* Schools List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-bold">Schools List</h3>
        </div>
        
        <div className="divide-y">
          {schools.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No schools found
            </div>
          ) : (
            schools.map(school => {
              const approval = approvals[school.school_id];
              const isApproved = approval?.nbfc_approved || false;
              
              return (
                <div key={school.school_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                      isApproved ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      {isApproved ? '✅' : '🏫'}
                    </div>
                    <div>
                      <h4 className="font-semibold">{school.name}</h4>
                      <p className="text-sm text-gray-500">
                        {school.address || school.city || 'No address'}
                      </p>
                      {isApproved && approval?.approved_at && (
                        <p className="text-xs text-green-600">
                          Approved on {new Date(approval.approved_at).toLocaleDateString('hi-IN')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Status Badge */}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isApproved 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isApproved ? '🏦 NBFC Enabled' : '⏳ Pending'}
                    </span>
                    
                    {/* Toggle Button */}
                    <button
                      onClick={() => toggleApproval(school.school_id, isApproved)}
                      disabled={updating === school.school_id}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        isApproved
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-500 text-white hover:bg-green-600'
                      } disabled:opacity-50`}
                    >
                      {updating === school.school_id ? (
                        '⏳...'
                      ) : isApproved ? (
                        '❌ Revoke'
                      ) : (
                        '✅ Approve'
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
